// ─────────────────────────────────────────────
//  ChimeMuteToggle — the per-user new-message chime mute control (Phase 5
//  messaging / UI-SPEC "New-Message Chime + Mute", D-03 + FLAG-4). Lives in the
//  MessagingPanel header slot.
//
//  • On by default; toggling persists to localStorage (FLAG-4) via the chime
//    module's setChimeMuted/isChimeMuted (per-device, no Cloud Functions).
//  • State is shown by ICON + TEXT, never color alone (WCAG 1.4.1):
//      🔔 Sound on   (default / not muted)
//      🔕 Muted      (pressed / muted)
//  • Carries an explicit accessible name + aria-pressed (a real toggle button),
//    and a 44px min touch target.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { isChimeMuted, setChimeMuted } from "../audio/chime";
import tokens from "../theme/tokens";

const { color, font, radius, space } = tokens;

export default function ChimeMuteToggle() {
  // Initialise from the persisted per-device preference (defaults to unmuted).
  const [muted, setMuted] = useState(() => isChimeMuted());

  const toggle = () => {
    const next = !muted;
    setMuted(next);
    setChimeMuted(next); // persist to localStorage (FLAG-4)
  };

  const label = muted ? "🔕 Muted" : "🔔 Sound on";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={muted}
      aria-label={muted ? "Message sound muted" : "Message sound on"}
      title={muted ? "Message chime is muted — click to unmute" : "Message chime is on — click to mute"}
      style={{ ...styles.btn, ...(muted ? styles.btnMuted : {}) }}
    >
      {label}
    </button>
  );
}

const styles = {
  btn: {
    minHeight: 44,
    display: "inline-flex",
    alignItems: "center",
    gap: space.xs,
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "0 12px",
    fontSize: font.size.label, // 12
    fontWeight: font.weight.bold, // 700
    color: color.text.secondary,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  // Muted state distinguished by icon + "Muted" TEXT (above), not color alone —
  // the subtle surface shift here is supplementary, never the sole signal.
  btnMuted: {
    background: color.surface.base,
    color: color.text.muted,
  },
};
